/**
 * Componente de Rota Protegida
 * Redireciona para login se não autenticado
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactElement;
  adminOnly?: boolean;
  allowedRoles?: string[];  // NOVO: Lista de roles permitidas
  excludeSuperuser?: boolean;  // NOVO: Excluir superuser desta rota
  superuserOnly?: boolean;
  allowPartner?: boolean;
}

export function PrivateRoute({ children, adminOnly = false, allowedRoles, excludeSuperuser = false, superuserOnly = false, allowPartner = false }: PrivateRouteProps) {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'partner' && !allowPartner) {
    return <Navigate to="/partner" replace />;
  }
  if (superuserOnly && !user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  // NOVO: Se excludeSuperuser=true e o usuário é superuser, bloquear
  if (excludeSuperuser && user?.is_superuser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-4">ℹ️ Página Não Disponível</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Esta página não está disponível para Super Administradores.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Super Admins gerenciam organizações globalmente através do Dashboard Global.
          </p>
          <button
            onClick={() => window.location.href = '/super-admin'}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Ir para Dashboard Global
          </button>
        </div>
      </div>
    );
  }

  // NOVA LÓGICA: Verificar roles permitidas
  if (allowedRoles && allowedRoles.length > 0) {
    const hasAllowedRole = user?.role && allowedRoles.includes(user.role);
    const isSuperuser = user?.is_superuser === true;
    
    // Se excludeSuperuser=true, superuser NÃO tem acesso mesmo estando em allowedRoles
    const canAccess = excludeSuperuser ? hasAllowedRole : (hasAllowedRole || isSuperuser);
    
    if (!canAccess) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-red-600 dark:text-red-400 mb-4">🔒 Acesso Negado</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Você não tem permissão para acessar esta página.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Apenas usuários com permissões específicas podem acessar esta área.
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
  }
  // Fallback para adminOnly (compatibilidade)
  else if (adminOnly && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-red-600 dark:text-red-400 mb-4">🔒 Acesso Negado</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Você não tem permissão para acessar esta página.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Apenas administradores podem acessar esta área.
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return children;
}
